import { Component, TemplateFunction } from '@ribajs/core'
import { hasChildNodesTrim } from '@ribajs/utils'

/**
 * The types for the scope
 */
interface Scope {
  // STATE
  isPaused: boolean

  // METHODS
  toggleAnimation: SbMarqueeToggleComponent['toggleAnimation']
  handleKeydown: SbMarqueeToggleComponent['handleKeydown']
  handleKeyup: SbMarqueeToggleComponent['handleKeyup']
}

/**
 * WCAG 2.2 compliant marquee toggle component
 * Allows users to pause/resume scrolling animations to meet Success Criterion 2.2.2 (Pause, Stop, Hide)
 *
 * Usage: <sb-marquee-toggle aria-label="Pause/resume animation">...</sb-marquee-toggle>
 */
export class SbMarqueeToggleComponent extends Component {
  /**
   * The tag name of the element
   */
  public static tagName = 'sb-marquee-toggle'

  /**
   * Initialization guard to prevent duplicate event listener attachment
   */
  private initialized = false

  /**
   * Everything that is assigned to the scope can be used within the template with the Riba template engine.
   */
  public scope: Scope = {
    // STATE
    isPaused: false,

    // METHODS
    toggleAnimation: this.toggleAnimation.bind(this),
    handleKeydown: this.handleKeydown.bind(this),
    handleKeyup: this.handleKeyup.bind(this),
  }

  /**
   * Attributes that can be passed to the custom element.
   */
  static get observedAttributes(): string[] {
    return []
  }

  /**
   * Attributes that must be passed to the custom element
   */
  protected requiredAttributes(): string[] {
    return []
  }

  /**
   * Called as soon as this custom element is registered
   */
  constructor() {
    super()
  }

  /**
   * Called as soon as this custom element is detected in the dom
   */
  protected connectedCallback() {
    super.connectedCallback()
    this.init(SbMarqueeToggleComponent.observedAttributes)
  }

  /**
   * Called as soon as this element is removed from the DOM
   */
  protected disconnectedCallback() {
    super.disconnectedCallback()
    this.removeEventListeners()
    // Reset initialization flag to allow proper re-connection
    this.initialized = false
  }

  /**
   * Called after this custom element is bound and ready
   */
  async afterBind() {
    await super.afterBind()

    // Only initialize once (guard against theme editor reloads)
    if (this.initialized) {
      return
    }

    this.setupAccessibility()
    this.addEventListeners()
    this.initialized = true

    this.classList.add(`${SbMarqueeToggleComponent.tagName}-ready`)
  }

  /**
   * Setup WCAG 2.2 accessibility attributes
   * Only sets attributes if not already present to respect integrator customizations
   */
  protected setupAccessibility() {
    // Make element interactive (only if not already set)
    if (!this.hasAttribute('role')) {
      this.setAttribute('role', 'button')
    }
    if (!this.hasAttribute('tabindex')) {
      this.setAttribute('tabindex', '0')
    }
    // aria-pressed always set to reflect current state
    this.setAttribute('aria-pressed', String(this.scope.isPaused))

    // Add visual indicator
    this.classList.add('cursor-pointer')
  }

  /**
   * Add event listeners scoped to this component instance
   */
  protected addEventListeners() {
    this.addEventListener('click', this.scope.toggleAnimation)
    this.addEventListener('keydown', this.scope.handleKeydown)
    this.addEventListener('keyup', this.scope.handleKeyup)
  }

  /**
   * Remove event listeners
   */
  protected removeEventListeners() {
    this.removeEventListener('click', this.scope.toggleAnimation)
    this.removeEventListener('keydown', this.scope.handleKeydown)
    this.removeEventListener('keyup', this.scope.handleKeyup)
  }

  /**
   * Toggle the animation state
   */
  public toggleAnimation() {
    this.scope.isPaused = !this.scope.isPaused

    // Toggle CSS class for animation control
    this.classList.toggle('paused', this.scope.isPaused)

    // Update ARIA attribute for screen readers
    this.setAttribute('aria-pressed', String(this.scope.isPaused))
  }

  /**
   * Handle keyboard interactions - Enter key on keydown (native button behavior)
   */
  public handleKeydown(event: KeyboardEvent) {
    // Ignore repeated key events when key is held down
    if (event.repeat) {
      return
    }

    // Enter activates on keydown (matches native button)
    if (event.key === 'Enter') {
      event.preventDefault()
      this.toggleAnimation()
    }
    // Prevent Space from scrolling the page (activation happens on keyup)
    else if (event.key === ' ' || event.key === 'Spacebar') {
      event.preventDefault()
    }
  }

  /**
   * Handle keyboard interactions - Space key on keyup (native button behavior)
   */
  public handleKeyup(event: KeyboardEvent) {
    // Space activates on keyup (matches native button)
    if (event.key === ' ' || event.key === 'Spacebar') {
      event.preventDefault()
      this.toggleAnimation()
    }
  }

  /**
   * Returns the template, if null is returned, the child html is not replaced
   */
  protected template(): ReturnType<TemplateFunction> {
    // Don't replace child HTML - this component wraps existing marquee content
    if (!hasChildNodesTrim(this)) {
      return (
        <p class="error">
          This component requires child HTML content (the marquee structure)
        </p>
      )
    }
    return null
  }
}
